// RiskAssessmentVisualization - Risk-ranked list with 7-level heatmap colors
// Implements US-5.4: Risk Assessment Matrix with Color Zones

import React, { useMemo } from 'react';
import { Item } from '../../types/domain';
import styles from './RiskAssessmentVisualization.module.css';

interface RiskAssessmentVisualizationProps {
  items: Item[];
}

/**
 * Risk result with calculated risk score and raw criteria
 */
interface RiskResult {
  itemId: string;
  title: string;
  riskScore: number;
  probability: number;
  impact: number;
}

/**
 * CALCULATE Risk Score for an item
 * Formula: Probability × Impact
 */
const calculateRiskScore = (item: Item): RiskResult | null => {
  // EXTRACT scores from item
  const probability = Number(
    item.scores?.find((s) => s.criterion === 'probability')?.value || 0
  );
  const impact = Number(item.scores?.find((s) => s.criterion === 'impact')?.value || 0);

  // VALIDATE: Only calculate if both criteria are scored
  if (probability === 0 || impact === 0) {
    return null;
  }

  // CALCULATE Risk score
  const riskScore = probability * impact;

  return {
    itemId: item.id,
    title: item.title,
    riskScore,
    probability,
    impact,
  };
};

/**
 * GET heatmap color based on risk score (7-level gradient)
 * NOTE: Red (highest risk) → Orange → Yellow → Yellowish-Green → Lime → Green (lowest risk)
 */
const getRiskColor = (riskScore: number): string => {
  // Level 7 (21-25): Critical - insur_tale Red
  if (riskScore >= 21) return '#e82a1f';

  // Level 6 (17-20): High - Orange
  if (riskScore >= 17) return '#f97316';

  // Level 5 (14-16): Medium-High - Light Orange
  if (riskScore >= 14) return '#fb923c';

  // Level 4 (11-13): Medium - Yellow
  if (riskScore >= 11) return '#fbbf24';

  // Level 3 (8-10): Medium-Low - Yellowish Green
  if (riskScore >= 8) return '#a3d639';

  // Level 2 (5-7): Low - Lime Green
  if (riskScore >= 5) return '#84cc16';

  // Level 1 (1-4): Very Low - insur_tale Green
  return '#276738';
};

/**
 * GET risk level label based on risk score
 */
const getRiskLevel = (riskScore: number): string => {
  if (riskScore >= 21) return 'Kritisch';
  if (riskScore >= 17) return 'Hoch';
  if (riskScore >= 14) return 'Mittel-Hoch';
  if (riskScore >= 11) return 'Mittel';
  if (riskScore >= 8) return 'Mittel-Niedrig';
  if (riskScore >= 5) return 'Niedrig';
  return 'Sehr niedrig';
};

/**
 * RISK ASSESSMENT VISUALIZATION component
 * - Displays risk-ranked list of scored items (highest risk first)
 * - Uses 7-level heatmap gradient for color coding
 * - Shows unscored items at bottom
 * - Shows empty state when no items scored
 */
export const RiskAssessmentVisualization: React.FC<RiskAssessmentVisualizationProps> = ({
  items,
}) => {
  /**
   * PROCESS items and calculate risk scores
   * NOTE: Memoized to avoid recalculating on every render
   */
  const { scoredResults, unscoredItems } = useMemo(() => {
    const scored: RiskResult[] = [];
    const unscored: Item[] = [];

    // SEPARATE scored and unscored items
    items.forEach((item) => {
      const result = calculateRiskScore(item);
      if (result) {
        scored.push(result);
      } else {
        unscored.push(item);
      }
    });

    // SORT scored items by risk score (highest first)
    scored.sort((a, b) => b.riskScore - a.riskScore);

    return { scoredResults: scored, unscoredItems: unscored };
  }, [items]);

  // RENDER empty state if no items are scored
  if (scoredResults.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateIcon}>⚠️</div>
        <h3>Füge Themen / Elemente und Bewertungen hinzu, um Ergebnisse zu sehen</h3>
        <p>Bewerte mindestens ein Thema / Element, um die Risikobewertung zu sehen</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* RISK-RANKED LIST SECTION */}
      <div className={styles.rankedList}>
        

        <div className={styles.listItems}>
          {scoredResults.map((result, index) => {
            const riskColor = getRiskColor(result.riskScore);
            const riskLevel = getRiskLevel(result.riskScore);

            return (
              <div
                key={result.itemId}
                className={styles.listItem}
                style={{ borderLeftColor: riskColor, borderLeftWidth: '6px' }}
              >
                {/* LEFT: Rank and Title */}
                <div className={styles.itemContent}>
                  <div className={styles.itemHeader}>
                    <span className={styles.rankNumber}>#{index + 1}</span>
                    <span className={styles.itemTitle} title={result.title}>
                      {result.title}
                    </span>
                  </div>

                  {/* Criteria Values with Risk Level */}
                  <div className={styles.criteriaInfo}>
                    <span className={styles.criteriaLabel}>Wahrscheinlichkeit:</span>
                    <span className={styles.criteriaValue}>{result.probability}</span>
                    <span className={styles.criteriaSeparator}>•</span>
                    <span className={styles.criteriaLabel}>Impact:</span>
                    <span className={styles.criteriaValue}>{result.impact}</span>
                    <span className={styles.criteriaSeparator}>•</span>
                    <span className={styles.riskLevel} style={{ color: riskColor }}>
                      {riskLevel}
                    </span>
                  </div>
                </div>

                {/* RIGHT: Risk Score Badge (compact) */}
                <div className={styles.riskInfo}>
                  <div
                    className={styles.riskScoreBadge}
                    style={{ backgroundColor: riskColor }}
                  >
                    <span className={styles.riskScoreValue}>{result.riskScore}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* UNSCORED ITEMS - Show at bottom if any */}
        {unscoredItems.length > 0 && (
          <div className={styles.unscoredSection}>
            <h4 className={styles.unscoredTitle}>Nicht bewertete Themen / Elemente ({unscoredItems.length})</h4>
            <div className={styles.unscoredList}>
              {unscoredItems.map((item) => (
                <div key={item.id} className={styles.unscoredItem}>
                  <span className={styles.unscoredIcon}>○</span>
                  <span className={styles.unscoredItemTitle}>{item.title}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* LEGEND SECTION */}
      <div className={styles.legendSection}>
        <h4 className={styles.legendTitle}>Risiko-Level Legende</h4>
        <div className={styles.legendGrid}>
          <div className={styles.legendItem}>
            <span className={styles.legendColor} style={{ backgroundColor: '#e82a1f' }}></span>
            <div className={styles.legendText}>
              <span className={styles.legendLabel}>Kritisch</span>
              <span className={styles.legendRange}>21-25</span>
            </div>
          </div>
          <div className={styles.legendItem}>
            <span className={styles.legendColor} style={{ backgroundColor: '#f97316' }}></span>
            <div className={styles.legendText}>
              <span className={styles.legendLabel}>Hoch</span>
              <span className={styles.legendRange}>17-20</span>
            </div>
          </div>
          <div className={styles.legendItem}>
            <span className={styles.legendColor} style={{ backgroundColor: '#fb923c' }}></span>
            <div className={styles.legendText}>
              <span className={styles.legendLabel}>Mittel-Hoch</span>
              <span className={styles.legendRange}>14-16</span>
            </div>
          </div>
          <div className={styles.legendItem}>
            <span className={styles.legendColor} style={{ backgroundColor: '#fbbf24' }}></span>
            <div className={styles.legendText}>
              <span className={styles.legendLabel}>Mittel</span>
              <span className={styles.legendRange}>11-13</span>
            </div>
          </div>
          <div className={styles.legendItem}>
            <span className={styles.legendColor} style={{ backgroundColor: '#a3d639' }}></span>
            <div className={styles.legendText}>
              <span className={styles.legendLabel}>Mittel-Niedrig</span>
              <span className={styles.legendRange}>8-10</span>
            </div>
          </div>
          <div className={styles.legendItem}>
            <span className={styles.legendColor} style={{ backgroundColor: '#84cc16' }}></span>
            <div className={styles.legendText}>
              <span className={styles.legendLabel}>Niedrig</span>
              <span className={styles.legendRange}>5-7</span>
            </div>
          </div>
          <div className={styles.legendItem}>
            <span className={styles.legendColor} style={{ backgroundColor: '#276738' }}></span>
            <div className={styles.legendText}>
              <span className={styles.legendLabel}>Sehr niedrig</span>
              <span className={styles.legendRange}>1-4</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
